import { useState, useCallback } from 'react';
import { X } from 'lucide-react';
import { usePWA } from '@/hooks/usePWA';
import { useIsMobile } from '@/hooks/useResponsive';
import { Button } from '@/components/ui';

const STORAGE_KEY = 'pwa-install-dismissed-at';
const DISMISS_DAYS = 7;
const PERMANENT_DISMISS = 'installed';

function isDismissed(): boolean {
  const value = localStorage.getItem(STORAGE_KEY);
  if (!value) return false;
  if (value === PERMANENT_DISMISS) return true;

  const dismissedAt = parseInt(value, 10);
  if (isNaN(dismissedAt)) return false;

  const daysSince = (Date.now() - dismissedAt) / (1000 * 60 * 60 * 24);
  return daysSince < DISMISS_DAYS;
}

function isIOSSafari(): boolean {
  const ua = navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !('MSStream' in window);
  return isIOS && /Safari/.test(ua) && !/CriOS|FxiOS/.test(ua);
}

export function PWAInstallBanner(): JSX.Element | null {
  const { canInstall, promptInstall, isStandalone } = usePWA();
  const isMobile = useIsMobile();
  const [dismissed, setDismissed] = useState(() => isDismissed());
  const [showIOSHint, setShowIOSHint] = useState(false);

  const isIOS = isIOSSafari();
  const shouldShow = isMobile && !isStandalone && !dismissed && (canInstall || isIOS);

  const handleDismiss = useCallback(() => {
    localStorage.setItem(STORAGE_KEY, String(Date.now()));
    setDismissed(true);
  }, []);

  const handleInstall = useCallback(async () => {
    const accepted = await promptInstall();
    if (accepted) {
      localStorage.setItem(STORAGE_KEY, PERMANENT_DISMISS);
      setDismissed(true);
    }
  }, [promptInstall]);

  const handleIOSClick = useCallback(() => {
    setShowIOSHint((prev) => !prev);
  }, []);

  if (!shouldShow) return null;

  return (
    <div className="fixed top-16 left-4 right-4 z-50 animate-slide-up">
      <div className="bg-[var(--color-primary-500)] text-white rounded-lg shadow-[var(--shadow-overlay)] p-4">
        <div className="flex items-center gap-3">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium">Установите приложение</p>
            <p className="text-xs opacity-80 mt-0.5">Для быстрого доступа и удобной работы</p>
          </div>
          {canInstall ? (
            <Button variant="secondary" size="sm" onClick={handleInstall}>
              Установить
            </Button>
          ) : (
            <Button variant="secondary" size="sm" onClick={handleIOSClick}>
              Как установить
            </Button>
          )}
          <button
            type="button"
            onClick={handleDismiss}
            className="text-white/70 hover:text-white transition-colors p-1 -mr-1 shrink-0"
            aria-label="Закрыть"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        {showIOSHint && isIOS && (
          <div className="mt-3 pt-3 border-t border-white/20 text-xs opacity-90">
            <p>1. Нажмите кнопку <strong>«Поделиться»</strong> (квадрат со стрелкой внизу экрана)</p>
            <p className="mt-1">2. Выберите <strong>«На экран Домой»</strong></p>
          </div>
        )}
      </div>
    </div>
  );
}
